import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import { useHistoricalInboundCampaigns } from '../api';
import { buildInboundColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { InboundCampaignRow } from '../types';

export function InboundCampaignHistoricalPage() {
  const userId = useAuthStore((s) => s.userId);
  const search = useHistoricalInboundCampaigns();
  const rows = search.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [campaignName, setCampaignName] = useState('');
  const [did, setDid] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Historical view is read-only — past campaigns can't be started/stopped.
  const columns = useMemo(() => buildInboundColumns({ hideActions: true }), []);

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        campaignName: campaignName.trim(),
        did: did.trim(),
      },
      { onError: () => toast.error('Could not fetch historical campaigns.') },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Inbound History"
        description="Look up completed inbound campaigns by date range, name, or DID."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Combine date range with optional name or DID match.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] lg:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="End date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Campaign name</Label>
              <Input
                id="name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="did">DID</Label>
              <Input
                id="did"
                value={did}
                onChange={(e) => setDid(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<InboundCampaignRow, unknown>
        tableId="campaigns:inbound:historical:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={hasSearched ? 'No campaigns found' : 'Pick filters to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range or clearing the name / DID filter.'
            : 'Set a date range and click Search.'
        }
        getRowId={(row) => String(row.campaignId)}
        initialColumnVisibility={DEFAULT_VISIBLE_COLUMNS}
        exportFilename="inbound-campaigns-historical.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
